import { z } from "zod";
import { SystemEntity } from "@erp/shared-types";
import { defineStepConfig } from "../../../registry/helpers";
import { conditionalSocket } from "../../../types";
import { entityOfType } from "../../../utils/system-entities";

/**
 * Classify Organization (OMS v3, routing org). Decides which of OUR
 * organizations an inbound customer PO was placed with (the **selling entity** —
 * the bill-to / sold-by / remit-to party the customer addressed, NOT the buyer).
 *
 * Input is just the draft sales order id — the step resolves the draft's inbound
 * email + its PO attachment(s) itself, and sends the email details + documents +
 * the candidate organizations to the model, which picks the matching org
 * (closed-set classification). No extraction schema to configure.
 *
 * By default it matches against EVERY other organization in the run's tenant;
 * turn `matchAllTenantOrgs` off to restrict to an explicit list (picked from the
 * tenant's organizations on the canvas — no raw ids to type).
 *
 * Outputs the matched organization id (feeds an emit_system_event step that
 * routes DRAFT_SALES_ORDER_ROUTED to it) when confident; otherwise takes the
 * `unclassified` socket so the draft stays in the routing org for a Gently-team
 * review action (→ reassign_draft_sales_order).
 *
 * Backend-only (AI classification + DB + audit); handler in
 * backend/src/workflows/steps/order-management/.
 */
const organizationRouteSchema = z.object({
  organizationId: z.string().uuid().describe("Target organization id"),
  label: z.string().optional().describe("Display label for the route"),
});

export const classifyOrganizationStep = defineStepConfig({
  type: "classify_organization",
  name: "Classify Organization",
  description:
    "Extracts the selling company from the draft's source document (via a chosen extraction schema) and routes the draft to the matching organization in the tenant",
  category: "Data Access",
  libraryModule: "OrderManagement",
  inputSchema: z.object({
    draftSalesOrderId: z
      .string()
      .uuid()
      .optional()
      .describe(
        "Draft to classify — the step reads its source document, logs to it, and routes it",
      ),
    // Alternative to draftSalesOrderId for flows that classify straight off an
    // email with no draft (e.g. the router's discount request branch): the step reads the
    // email's attachments + thread context directly. Ignored when a draft id
    // is provided. Accepts an entity reference or a bare uuid (coerced), so
    // classify_document's inboundEmail pass-through wires straight in.
    inboundEmail: entityOfType(SystemEntity.INBOUND_EMAIL)
      .optional()
      .describe(
        "Inbound email to classify from directly (used when there is no draft, e.g. discount request routing)",
      ),
  }),
  outputSchema: z.object({
    // Taken only when a confident match is found — feeds emit_system_event's
    // targetOrganizationId.
    matched_organization_id: conditionalSocket(z.string().uuid())
      .optional()
      .describe("Matched organization id (taken only on a confident match)"),
    // Draft id, taken only on a confident match. Feeds the cross-org routing
    // emit's REQUIRED draftSalesOrderId so that emit step is dead-pruned on the
    // unclassified branch (it must not publish DRAFT_SALES_ORDER_ROUTED for a
    // draft that didn't match an org). Symmetric to `unclassified` below.
    classified: conditionalSocket(z.string().uuid())
      .optional()
      .describe(
        "Routed entity id (draft id, or inbound email id in email mode), taken on a confident match (feeds the cross-org routing emit)",
      ),
    matched_organization_name: z.string().nullable(),
    confidence: z.number().describe("Match confidence 0..1"),
    // Taken when no confident match — carries the draft id for the review route.
    unclassified: conditionalSocket(z.string().uuid())
      .optional()
      .describe(
        "Routed entity id (draft id, or inbound email id in email mode), taken when no organization matched confidently (route to manual review)",
      ),
  }),
  configSchema: z.object({
    // When true (default), match against EVERY other organization in the run's
    // tenant. When false, match only the explicitly-listed organizations.
    matchAllTenantOrgs: z
      .boolean()
      .optional()
      .default(true)
      .describe(
        "Match against all organizations in the tenant (default). Disable to match only the explicit list below.",
      ),
    organizationRoutes: z
      .array(organizationRouteSchema)
      .optional()
      .describe(
        "Explicit candidate organizations (picked from the tenant's orgs). Used when matchAllTenantOrgs is off.",
      ),
    confidenceThreshold: z
      .number()
      .min(0)
      .max(1)
      .optional()
      .default(0.6)
      .describe("Minimum match confidence to classify (else unclassified)"),
  }),

  emits: [] as const,

  execution: {
    startToCloseTimeout: "2 minutes",
    retry: {
      maximumAttempts: 2,
    },
  },
});
